import React, { useEffect, useRef, useState } from 'react';
import { ContactInfo, COUNTERS } from '../Utils/Data';
import { Link } from 'react-router-dom';
import { useLanguage } from '../i18n/LanguageContext';
import { ABOUT_PAGE } from '../i18n/aboutCopy';
import { FAQ_REACH_US, HOME_PAGE } from '../i18n/homeCopy';
import { HOME_SECTIONS } from '../i18n/sectionCopy';
import { useLocalizedStatic } from '../i18n/useLocalizedStatic';
import { getServices } from '../Utils/api';
import { mapServiceHomeItem } from '../Utils/serviceHelpers';
import HomeBlogSection from './include/HomeBlogSection';
import HomeProjectsSection from './include/HomeProjectsSection';
import HomeReviewsSection from './include/HomeReviewsSection';
import LetsTalkSection from './include/LetsTalkSection';
import HeroVideo from './include/HeroVideo';
import { SITE_REPRESENTATIVE_IMAGE, BENEFIT_SECTION_IMAGE } from '../Utils/siteConfig';
import PageSEO from '../Components/PageSEO';
import { useStaticSeo } from '../Utils/useStaticSeo';

const img = (path) => `${process.env.PUBLIC_URL}/assets/images/${path}`;

const CounterNumber = ({ to, active }) => {
  const [count, setCount] = useState(0);

  useEffect(() => {
    if (!active) return undefined;
    let frame;
    const duration = 3000;
    const start = performance.now();
    const tick = (now) => {
      const progress = Math.min((now - start) / duration, 1);
      setCount(Math.floor(progress * to));
      if (progress < 1) frame = requestAnimationFrame(tick);
    };
    frame = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(frame);
  }, [active, to]);

  return <span className="rs-count">{count}</span>;
};

const FaqReachUsBody = () => {
  const labels = useLocalizedStatic(FAQ_REACH_US);
  const whatsappNumber = ContactInfo.contact_phone.replace(/\D/g, '');

  return (
    <div className="faq-reach-us">
      <p>{labels.intro}</p>
      <ul className="faq-contact-list">
        <li>
          <strong>{labels.officeLabel}</strong> {ContactInfo.address}
        </li>
        <li>
          <strong>{labels.mapLabel}</strong>{' '}
          <Link to="/contact">{labels.mapLink}</Link>
        </li>
        <li>
          <strong>{labels.emailLabel}</strong>{' '}
          <a href={`mailto:${ContactInfo.contact_email}`}>{ContactInfo.contact_email}</a>
        </li>
        <li>
          <strong>{labels.phoneLabel}</strong>{' '}
          <a href={`tel:${ContactInfo.contact_phone}`}>{ContactInfo.contact_phone}</a>
          {' · '}
          <a href={`tel:${ContactInfo.contact_phone2}`}>{ContactInfo.contact_phone2}</a>
        </li>
        <li>
          <strong>{labels.whatsappLabel}</strong>{' '}
          <a
            href={`https://wa.me/${whatsappNumber}`}
            target="_blank"
            rel="noopener noreferrer"
          >
            {ContactInfo.contact_phone}
          </a>
        </li>
        <li>
          <strong>{labels.hoursLabel}</strong> {ContactInfo.office_hours}
        </li>
      </ul>
    </div>
  );
};

const FAQ_ITEMS = [
  {
    id: 'collapseOne',
    title: 'What contracting services does Q-Tech offer in Saudi Arabia?',
    body:
      'Q-Tech Contracting is a general contractor headquartered in Al Khobar, delivering civil works, mechanical, electrical, instrumentation, insulation, boiler, and industrial construction across Dammam, Jubail, Ras Al-Khair, Yanbu, and the wider Kingdom.',
    last: false,
  },
  {
    id: 'collapseTwo',
    title: 'Is Q-Tech a Saudi contracting company?',
    body:
      'Yes. Q-Tech Contracting was established in 2023 under Al Dawood Holding Group — a Saudi construction and trading group founded in 1998 with offices in Al Khobar, Jubail, and Yanbu. That backing gives clients financial stability and resources for complex multi-discipline programmes.',
    last: false,
  },
  {
    id: 'collapseThree',
    title: 'How does Q-Tech manage construction projects in KSA?',
    body:
      'From planning through handover, our project managers coordinate civil, mechanical, and E&I crews on site with clear reporting and accountable supervision. We take on sensitive work — occupied facilities, fast-track outages, and limited-access sites — without passing responsibility to others.',
    last: false,
  },
  {
    id: 'collapseFour',
    title: 'How can I contact Q-Tech Contracting in Al Khobar?',
    contact: true,
    last: true,
  },
];

const Home = () => {
  const { locale, revision } = useLanguage();
  const counterRef = useRef(null);
  const faqItems = useLocalizedStatic(FAQ_ITEMS);
  const counters = useLocalizedStatic(COUNTERS);
  const aboutCopy = useLocalizedStatic(ABOUT_PAGE);
  const copy = useLocalizedStatic(HOME_PAGE);
  const homeSections = useLocalizedStatic(HOME_SECTIONS);
  const seo = useStaticSeo('home', '/');
  const [activeFaq, setActiveFaq] = useState('collapseOne');
  const [countersActive, setCountersActive] = useState(false);
  const [homeServices, setHomeServices] = useState([]);
  const [servicesLoading, setServicesLoading] = useState(true);
  const [servicesFromApi, setServicesFromApi] = useState(false);

  useEffect(() => {
    let active = true;
    setServicesLoading(true);

    getServices()
      .then((data) => {
        if (!active) return;
        setHomeServices((data || []).map(mapServiceHomeItem));
        setServicesFromApi(true);
      })
      .catch((err) => {
        if (!active) return;
        console.warn('Failed to load home services:', err?.message || err);
        setHomeServices([]);
        setServicesFromApi(false);
      })
      .finally(() => {
        if (active) setServicesLoading(false);
      });

    return () => {
      active = false;
    };
  }, [locale, revision]);

  useEffect(() => {
    const $ = window.jQuery;
    if ($?.fn) {
      $.fn.counterUp = function noopCounterUp() { return this; };
      $('.rs-count').each(function destroyCounterWaypoint() {
        const $el = $(this);
        $el.removeData('counterup-nums counterup-func counterup-init');
        try {
          $el.waypoint('destroy');
        } catch {
           
        }
      });
    }
  }, []);

  useEffect(() => {
    const node = counterRef.current;
    if (!node) {
      return;
    }

    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setCountersActive(true);
          observer.disconnect();
        }
      },
      { threshold: 0.2 },
    );

    observer.observe(node);
    return () => observer.disconnect();
  }, []);

  return (
    <>
    <PageSEO {...seo} />
    <h1 className="sr-only">
      {locale === 'ar'
        ? 'شركة مقاولات في السعودية — كيو-تك للمقاولات | مقاولات عامة مدنية وميكانيكية وكهربائية في الخبر'
        : 'Contracting Company Saudi Arabia — Q-Tech Contracting | General Contractor Al Khobar'}
    </h1>
    <HeroVideo />




    <div className="rs-about about-style1 bg1 pt-120 pb-120 md-pt-80 md-pb-80">
      <div className="container">
        <div className="row y-middle">
          <div className="col-lg-6 md-mb-50">
            <div className="images-part">
              <img src={img('about/style1/fahad.jpg')} alt={aboutCopy.chairmanName} loading="lazy" decoding="async" />
            </div>
          </div>
          <div className="col-lg-6 pl-50 md-pl-15">
            <div className="sec-title">
              <span className="sub-text">
                <img src={img('shape-1.png')} alt="Images" />
                {aboutCopy.whoWeAre}
              </span>
              <h2 className="title pb-20">
                {aboutCopy.groupTitle}
              </h2>
              <p className="desc pb-20">
                {aboutCopy.intro1Before}{' '}
                <a href="https://samec.com.sa/" target="_blank" rel="noopener noreferrer">SAMEC</a>
                {aboutCopy.intro1After}
              </p>
              <p className="desc pb-20">
                {aboutCopy.intro2}
              </p>
              <h3 className="title pb-15" style={{ fontSize: '22px' }}>
                {aboutCopy.quoteTitle}
              </h3>
              <p className="desc pb-37" style={{ fontStyle: 'italic' }}>
                &ldquo;{aboutCopy.quote}&rdquo;
              </p>
              <ul className="check-lists">
                <li className="list-item">
                  <span className="icon-list-icon">
                    <i className="fa fa-check-circle" />
                  </span>
                  <span className="list-text">{aboutCopy.check1}</span>
                </li>
                <li className="list-item">
                  <span className="icon-list-icon">
                    <i className="fa fa-check-circle" />
                  </span>
                  <span className="list-text">{aboutCopy.check2}</span>
                </li>
                <li className="list-item">
                  <span className="icon-list-icon">
                    <i className="fa fa-check-circle" />
                  </span>
                  <span className="list-text">{aboutCopy.check3}</span>
                </li>
              </ul>
              <div className="row mt-47 md-mt-25">
                <div className="col-lg-6 col-md-4 sm-mb-30">
                  <div className="btn-part">
                    <a className="readon more know" href="/about">
                      {aboutCopy.knowMore}
                    </a>
                  </div>
                </div>
                <div className="col-lg-6 col-md-8">
                  <div className="signeture-img">
                    <p className="mb-0" style={{ fontWeight: 600, fontSize: '18px' }}>
                      {aboutCopy.chairmanName}
                    </p>
                    <span>{aboutCopy.chairmanRole}</span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>


    <div className="rs-services services-main-home gray-bg pt-120 pb-120 md-pt-80 md-pb-80">
  <div className="container">
    <div className="sec-title text-center mb-55 md-mb-35">
      <span className="sub-text">
        <img src={img('shape-1.png')} alt="Images" />
        {copy.whatWeDo}
      </span>
      <h2 className="title">
        {copy.servicesTitleLine1}
        <br />
        {copy.servicesTitleLine2}
      </h2>
    </div>
    {servicesLoading && (
      <p className="text-center py-4">{homeSections.servicesLoading}</p>
    )}
    {!servicesLoading && servicesFromApi && homeServices.length === 0 && (
      <p className="text-center py-4">{homeSections.servicesEmpty}</p>
    )}
    {!servicesLoading && !servicesFromApi && (
      <p className="text-center py-4 text-danger">{homeSections.servicesLoadError}</p>
    )}
    {!servicesLoading && homeServices.length > 0 && (
    <div className="row">
      {homeServices.map((item, index) => (
        <div
          key={item.id}
          className={`col-xl-4 col-md-6${index < 3 ? ' mb-30' : ''}${index === 3 ? ' md-mb-30' : ''}${index === 4 ? ' sm-mb-30' : ''}`}
        >
          <div className="services-item">
            <div className="services-wrap">
              <div className="services-image">
                <img src={item.image} alt={item.title} loading="lazy" decoding="async" />
              </div>
              <div className="services-content">
                <div className="service-inner">
                  <div className="icon-top">
                    <img src={item.icon} alt={item.title} />
                  </div>
                  <div className="services-titles">
                    <h3 className="title">
                      <Link to={item.link}>{item.title}</Link>
                    </h3>
                  </div>
                  <p className="services-txt">{item.description}</p>
                  <div className="services-btn">
                    <Link className="btn-text" to={item.link}>
                      {copy.readMore}
                    </Link>
                  </div>
                </div>
              </div>
              <div className="normarl-text-area">
                <div className="services-titles">
                  <h3 className="title">
                    <Link to={item.link}>{item.title}</Link>
                  </h3>
                </div>
                <div className="icon-image">
                  <img src={item.icon} alt={item.title} />
                </div>
              </div>
            </div>
          </div>
        </div>
      ))}
    </div>
    )}
  </div>
</div>

       

      <div ref={counterRef} className="rs-counter counter-style1 bg2 pt-120 pb-120 md-pt-80 md-pb-80">
        <div className="container">
          <div className="row y-middle">
            <div className="col-lg-6 md-mb-50">
              <div className="sec-title">
                <span className="sub-text yellow-color">
                  <img src={img('shape-1.png')} alt="Images" />
                  {copy.numberTalks}
                </span>
                <h2 className="title white-color pb-20">
                  {copy.counterTitleLine1}
                  <br />
                  {copy.counterTitleLine2}
                </h2>
                <p className="desc desc2 pb-45">
                  {copy.counterDesc}
                </p>
                <div className="btn-part">
                  <a className="readon more contact" href="/contact">
                    {copy.contactNow}
                  </a>
                </div>
              </div>
            </div>
            <div className="col-lg-6">
              <div className="row">
                {counters.map((item, index) => (
                  <div
                    key={item.label}
                    className={`col-xl-6 col-md-6 col-sm-6${index < 2 ? ' mb-30' : ''}${index === 2 ? ' xs-mb-30' : ''}`}
                  >
                    <div className="rs-counter-list">
                      <div className="count-text">
                        <div className="count-number">
                          <CounterNumber to={item.value} active={countersActive} />
                          <span className="prefix">+</span>
                        </div>
                        <span className="title">{item.label}</span>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </div>




<div className="rs-faq faq-style1">
  <div className="row">
    <div className="col-lg-4 offset-2 faq-offset6 md-mb-50">
      <div className="faq-content">
        <div className="sec-title mb-40">
          <span className="sub-text">
            <img src={img('shape-1.png')} alt="Images" />
            {copy.yourBenefit}
          </span>
          <h2 className="title">
            {copy.benefitTitleLine1}
            <br />
            {copy.benefitTitleLine2}
          </h2>
        </div>
        <div id="accordion" className="accordion">
          {faqItems.map((item) => {
            const isOpen = activeFaq === item.id;
            return (
              <div className="card" key={item.id}>
                <div className="card-header">
                  <a
                    className={`card-link${isOpen ? '' : ' collapsed'}${item.last ? ' last-margin' : ''}`}
                    href={`#${item.id}`}
                    role="button"
                    aria-expanded={isOpen}
                    onClick={(e) => {
                      e.preventDefault();
                      setActiveFaq(item.id);
                    }}
                  >
                    {item.title}
                  </a>
                </div>
                {isOpen && (
                  <div id={item.id}>
                    <div className="card-body">
                      {item.contact ? <FaqReachUsBody /> : item.body}
                    </div>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      </div>
    </div>
    <div className="col-lg-6 faq-images md-offset-2">
      <div className="faq-image-wrap">
        <img
          src={BENEFIT_SECTION_IMAGE}
          alt="Q-Tech office reception, Al Buhairah, Al Khobar"
          className="faq-side-photo"
          loading="lazy"
          decoding="async"
        />
      </div>
    </div>
  </div>
</div>



 
      <HomeProjectsSection />





      <div className="rs-choose choose-style1">
  <div className="row no-gutters">
    <div className="col-lg-6 d-none d-lg-block video-item home-choose-image">
      <div className="home-choose-image__wrap">
        <img
          src={SITE_REPRESENTATIVE_IMAGE}
          alt={locale === 'ar' ? 'كيو-تك للمقاولات — شركة مقاولات في الخبر' : 'Q-Tech Contracting — general contractor Saudi Arabia'}
          className="home-choose-image__photo"
          loading="lazy"
          decoding="async"
        />
      </div>
    </div>
    <div className="col-lg-6">
      <div className="widget-wrap-content">
        <div className="sec-title mb-46 md-mb-25">
          <span className="sub-text">
            <img src={img('shape-1.png')} alt="Images" />
            {copy.whyChooseUs}
          </span>
          <h2 className="title pb-20">
            {copy.whyTitleLine1}
            <br />
            {copy.whyTitleLine2}
          </h2>
          <p className="desc">
            {copy.whyDesc}
          </p>
        </div>
        <div className="row">
          <div className="col-lg-6 mb-25">
            <div className="rs-addon-services">
              <div className="services-item">
                <div className="serial-number">1</div>
                <div className="services-text">
                  <div className="services-title">
                    <h4 className="title"> {copy.bestSustainability}</h4>
                  </div>
                </div>
              </div>
            </div>
          </div>
          <div className="col-lg-6 mb-25">
            <div className="rs-addon-services">
              <div className="services-item">
                <div className="serial-number">2</div>
                <div className="services-text">
                  <div className="services-title">
                    <h4 className="title">{copy.healthSafety}</h4>
                  </div>
                </div>
              </div>
            </div>
          </div>
          <div className="col-lg-6 md-mb-25">
            <div className="rs-addon-services">
              <div className="services-item">
                <div className="serial-number">3</div>
                <div className="services-text">
                  <div className="services-title">
                    <h4 className="title">{copy.modernMethods}</h4>
                  </div>
                </div>
              </div>
            </div>
          </div>
          <div className="col-lg-6">
            <div className="rs-addon-services">
              <div className="services-item">
                <div className="serial-number">4</div>
                <div className="services-text">
                  <div className="services-title">
                    <h4 className="title">{copy.certifiedEngineer}</h4>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
        <div className="working-skill-part pt-50">
          <div className="row y-middle">
            <div className="col-lg-4">
              <div className="title-inner mb-10">
                <h4 className="title">{copy.workingSkill}</h4>
              </div>
              <div className="title-inner">
                <h4 className="title">{copy.clientRating}</h4>
              </div>
            </div>
            <div className="col-lg-8">
              <div className="rs-skillbar skillbar-style1 mb-10">
                <div className="cl-skill-bar">
                  {/* Start Skill Bar */}
                  <div className="skillbar" data-percent={95}>
                    <p className="skillbar-bar" style={{ width: "95%" }} />
                    <span className="skill-bar-percent">95%</span>
                  </div>
                </div>
              </div>
              <ul className="icon-list-item">
                <li>
                  <i className="fa fa-star" />
                </li>
                <li>
                  <i className="fa fa-star" />
                </li>
                <li>
                  <i className="fa fa-star" />
                </li>
                <li>
                  <i className="fa fa-star" />
                </li>
                <li>
                  <i className="fa fa-star" />
                </li>
              </ul>
            </div>
          </div>
          <p className="view-result pt-15">
            {copy.viewResultPrefix}{' '}
            <Link to="/contact">
              <span>{copy.moreInfo}</span>
            </Link>
          </p>
        </div>
      </div>
    </div>
  </div>
</div>


      <HomeReviewsSection />





      <LetsTalkSection copy={copy} sourcePage="home" />




<HomeBlogSection />


      <div className="rs-cta bg4 pt-120 pb-115 md-pt-80 md-pb-75">
        <div className="container">
          <div className="call-action">
            <div className="sec-title text-center">
              <span className="sub-text yellow-color">
                <img src={img('shape-1.png')} alt="Images" />
                {copy.workWithUs}
              </span>
              <h2 className="title white-color pb-20">{copy.ctaTitle}</h2>
              <p className="desc desc3 pb-35">
                {copy.ctaDescLine1}
                <br />
                {copy.ctaDescLine2}
              </p>
              <div className="btn-part">
                <a className="readon more contact" href="/contact">
                  {copy.getStarted}
                </a>
              </div>
            </div>
          </div>
        </div>
      </div>





    </>
  );
};

export default Home;
